import * as ko from 'knockout';
import {_} from 'lodash';
import {
    ICompanyLogin,
    IMessageData,
    IObjectData,
    IOrganization,
    IOverride,
    IRouteDefinitionParams,
} from '../../../index';
import {ObjectTypeId} from '../../../constants';
import {REQUEST_MESSAGE} from '../../../constants/Language';
import {global, lm} from '../../../appContext';
import {createErrorMessage, getNextId} from '../../../util/misc';
import {nsRoute} from '../../../util/route';
import {nsTooltip} from '../../../util/nsTooltip';
import {storeObservable} from '../../../util/sessionstore';
import {ConcurrentRequestQueue} from '../../../req/ConcurrentRequestQueue';
import {nsSyncFields} from '../../../req/nsSyncFields';
import {IComboboxV2Options} from '../../combobox/v2';
import {nsFa} from '../../fa/fn';
import {IBulkActionBarParams, IBulkActionCtx} from '../../bulk-action-bar/bulk-action-bar';
import {DEPARTMENT_SEARCH_COLS, OrganizationPanel} from './OrganizationPanel';
import {IPermissionRowActions, PermissionRow} from './PermissionRow';
import {PermissionMutations, ManualSpOp, permissionSnackbar} from './permissionMutations';
import {nsUserPermissions} from './nsUserPermissions';
import {nsUserPermissionPolicy} from './nsUserPermissionPolicy';
import {nsOverride} from '../../override/nsOverride';
import isOverridePendingDeletion = nsOverride.isOverridePendingDeletion;
import getCancelPendingDeletionConfirmKind = nsOverride.getCancelPendingDeletionConfirmKind;
import {KOSubscriptionTracker} from '../../../util/KOSubscriptionTracker';
import detailsHtml from './details.html?raw';
import i = nsFa.i;
import ParsedRouteDataDecorator = nsRoute.ParsedRouteDataDecorator;

export let template = detailsHtml;

const STATUS_FILTER_OPTIONS: ReadonlyArray<{
    tab: nsUserPermissions.TPermissionStatusFilter;
    labelKey: string;
}> = [
    {tab: 'current', labelKey: 'user.permissions.tabs.current'},
    {tab: 'pending', labelKey: 'user.permissions.tabs.pending'},
    {tab: 'removed', labelKey: 'user.permissions.tabs.removed'},
];

/**
 * Permissions-v2 page: load user, org panels, edit exclusivity / busy UI.
 * Persist flows live in {@link PermissionMutations}.
 */
export class viewModel extends ParsedRouteDataDecorator {
    private readonly isReady: KnockoutObservable<boolean> = ko.observable(false);
    private readonly messageData: KnockoutObservable<IMessageData | null> = ko.observable(null);
    private readonly organizations: KnockoutObservableArray<OrganizationPanel> = ko.observableArray<OrganizationPanel>([]);
    private readonly searchSystemQueue = new ConcurrentRequestQueue();
    private readonly mutations = new PermissionMutations();
    private user!: IObjectData;
    private departmentSyncfieldIds = '';

    private readonly isBusy = ko.observable(false);
    private readonly editingRow = ko.observable<PermissionRow | null>(null);
    private readonly isBulkEditing = ko.observable(false);
    private readonly selectedPermissions = ko.observableArray<PermissionRow>([]);
    private readonly selectionKosub = new KOSubscriptionTracker();
    /** Derived from row checkboxes; drives `selectedPermissions` for the bulk-action-bar. */
    private readonly selectedRowsComputed = ko.pureComputed(() => this.collectSelectedRows());
    /** Inline add-tenant form below the footer Add tenant button. */
    private readonly isAddTenantFormOpen = ko.observable(false);
    private readonly selectedAddTenantOrganization = ko.observable<IOrganization | null>(null);
    private readonly availableAddTenantOrganizations = ko.observableArray<IOrganization>([]);
    /** Owned so cancel/reopen clears the field; v2 would otherwise reuse a written-back input on remount. */
    private readonly addTenantComboboxInput = ko.observable('');
    /** Page status filter shared by all org panels (Current / Pending / Added / Removed). */
    readonly permissionStatusFilter = ko.observable<nsUserPermissions.TPermissionStatusFilter>('current');
    readonly statusFilterOptions = STATUS_FILTER_OPTIONS;
    readonly isStatusMenuOpen = ko.observable(false);
    readonly statusMenuId = `permissions-v2-status-${getNextId()}`;
    readonly statusMenuPanelId = `${this.statusMenuId}-menu`;
    private readonly statusAnchorName = `--${this.statusMenuId}`;
    readonly statusAnchorStyle = {'anchor-name': this.statusAnchorName};
    readonly statusMenuStyle = {'position-anchor': this.statusAnchorName};
    readonly selectedStatusLabelKey = ko.pureComputed(() => {
        const tab = this.permissionStatusFilter();
        return STATUS_FILTER_OPTIONS.find(o => o.tab === tab)?.labelKey
            ?? 'user.permissions.tabs.current';
    });
    /** Cross-org totals per status filter (from each panel’s SP + overrides). */
    readonly statusTabCounts = ko.pureComputed(() => {
        const counts: Record<nsUserPermissions.TPermissionStatusFilter, number> = {
            current: 0,
            pending: 0,
            removed: 0,
        };
        for (const panel of this.organizations()) {
            for (const tab of STATUS_FILTER_OPTIONS) {
                counts[tab.tab] += panel.countMatchingFilter(tab.tab);
            }
        }
        return counts;
    });
    readonly selectedStatusBadge = ko.pureComputed(() => {
        return String(this.statusTabCounts()[this.permissionStatusFilter()]);
    });
    /** Own combobox/v2 options (not ruleset target-org-combobox). */
    private readonly addTenantComboboxParams: IComboboxV2Options<IOrganization> = {
        prepareData: {
            map: (arr) => {
                const companylogin_selected = storeObservable('selected.company.login')() as ICompanyLogin;
                if (companylogin_selected?.vanityindex) {
                    const mapped = _.cloneDeep(arr);
                    const item = mapped.find(
                        (o: IOrganization) => o.organizationid == companylogin_selected.orgnr,
                    );
                    if (item) {
                        item.organizationame = companylogin_selected.name;
                    }
                    return mapped;
                }
                return arr;
            },
        },
        inputAttrs: {
            placeholder: lm.t('user.permissions.choose.organization'),
            title: lm.t('user.permissions.choose.organization'),
            ariaLabel: lm.t('user.permissions.choose.organization'),
        },
        wrapperClass: 'combobox-organization',
        enable: true,
        option: {
            label: (o) => o?.organizationame ?? '',
            key: (o) => o.organizationid,
            tooltip: (o) => o ? (lm.t(nsTooltip.getTitle_organization(o)) ?? '') : '',
        },
        initialMatch: (o) => !!o && o.organizationid == this.selectedAddTenantOrganization()?.organizationid,
        restoreLastValidOnBlur: true,
        lazyMountList: true,
        input: this.addTenantComboboxInput,
        selected: this.selectedAddTenantOrganization,
        data: this.availableAddTenantOrganizations,
    };
    private readonly canMutateRows = ko.pureComputed(() => !this.isBusy());
    /**
     * Empty info alert: no visible permissions yet. Still shown when only the sticky
     * selected-org shell is present (so Add permission sits under the alert).
     */
    private readonly showEmptyPermissionsAlert = ko.pureComputed(() => {
        return !this.organizations().some(org => {
            org.dataRevision();
            return org.hasVisiblePermissions();
        });
    });
    /** Page-scoped: add-tenant form, ephemeral tenant, and/or any org with draft New Permission rows. */
    private readonly isAddMode = ko.pureComputed(() => {
        return this.isAddTenantFormOpen() || this.organizations().some(
            org => org.isEphemeralTenant() || org.newPermissionRows().length > 0,
        );
    });
    /** True while a single existing permission row is in edit mode (not bulk). */
    private readonly isRowEditing = ko.pureComputed(() => this.editingRow() != null);
    /** No add / row-edit / bulk-edit — object links and similar read-only chrome. */
    private readonly isReadMode = ko.pureComputed(() => {
        return !this.isAddMode() && !this.isBulkEditing() && this.editingRow() == null;
    });
    /** Hide checkboxes / drop selection (add form or single-row edit). Bulk edit freezes checkboxes via panel `isSelectionDisabled`. */
    private readonly isSelectionLocked = ko.pureComputed(() => this.isAddMode() || this.isRowEditing());
    /** Block starting drafts while an existing row is being edited or the page is busy. */
    private readonly canMutateDrafts = ko.pureComputed(() => {
        return !this.isBusy() && this.editingRow() == null && !this.isBulkEditing();
    });
    /** Add Tenant shares the same gates as other mutate actions, plus exclusive add-mode. */
    private readonly canAddTenant = ko.pureComputed(() => {
        if (!this.canMutateDrafts() || this.isAddMode()) {
            return false;
        }
        const presentIds = this.visibleOrganizationIds();
        return (global.login.organizations() ?? []).some(
            (o: IOrganization) => !presentIds.has(o.organizationid),
        );
    });
    private readonly canConfirmAddTenant = ko.pureComputed(() => {
        return this.isAddTenantFormOpen() && !!this.selectedAddTenantOrganization() && !this.isBusy();
    });

    private readonly rowActions: IPermissionRowActions = {
        canMutateRows: this.canMutateRows,
        isAddMode: this.isAddMode,
        isBulkEditing: this.isBulkEditing,
        isReadMode: this.isReadMode,
        editingRow: this.editingRow,
        beginEdit: (row) => this.beginEdit(row),
        cancelEdit: (row) => this.cancelEdit(row),
        saveEdit: (row) => this.saveEdit(row),
        requestDelete: (row) => this.requestDelete(row),
        requestRestore: (row) => this.requestRestore(row),
        requestCopy: (row) => this.requestCopy(row),
        savePermissionComment: (row) => this.savePermissionComment(row),
        getSiblingIdentities: (row) => this.getSiblingIdentities(row),
        getOccupancySlots: (row) => {
            const panel = this.findPanelForRow(row);
            return panel?.getOccupancySlots({exceptRow: row}) ?? [];
        },
    };

    /** Stable params for the bulk-action-bar (validators/enabled close over page state). */
    private readonly bulkActionBarParams: IBulkActionBarParams<PermissionRow> = {
        selected: this.selectedPermissions,
        selectedLabel: () => `user.permissions.bulk.label.selected|n:${this.selectedPermissions().length}`,
        onClose: () => this.clearPermissionSelection(),
        operations: [
            {
                label: 'user.permissions.bulk.btn.edit',
                icon: 'fa-pencil',
                validator: () => !this.isBusy() && !this.isAddMode() && !this.editingRow(),
                enabled: () => this.selectedPermissions().every(r => r.canEditAction()),
                intermediate: {
                    label: () => `user.permissions.bulk.label.editing|n:${this.selectedPermissions().length}`,
                    saveEnabled: () => {
                        const rows = this.selectedPermissions();
                        return rows.some(r => r.isDirty())
                            && rows.every(r => !r.isDirty() || r.canSave());
                    },
                    onEnter: (rows) => this.beginBulkEdit(rows),
                    onCancel: (rows) => this.cancelBulkEdit(rows),
                    onSave: (rows, ctx) => this.saveBulkEdit(rows, ctx),
                },
            },
            {
                label: 'user.permissions.bulk.btn.delete',
                icon: 'fa-trash-alt',
                clearSelection: false,
                validator: () => !this.isBusy() && !this.isAddMode() && !this.editingRow() && !this.isBulkEditing(),
                enabled: () => this.selectedPermissions().every(r => r.canDeleteAction()),
                onExecute: (rows, ctx) => this.requestBulkDelete(rows, ctx),
            },
        ],
    };

    private readonly headingKey = this.__objectTypeId === ObjectTypeId.ApiUser
        ? 'user.permissions.apiuser.title'
        : 'user.permissions.title';
    private readonly descriptionKey = this.__objectTypeId === ObjectTypeId.ApiUser
        ? 'user.permissions.apiuser.description'
        : 'user.permissions.description';

    constructor(params: IRouteDefinitionParams) {
        super(params);
        this.selectionKosub.subscribe(this.selectedRowsComputed, (rows) => {
            this.selectedPermissions(rows);
        });
        this.selectionKosub.subscribe(this.isSelectionLocked, (locked) => {
            if (locked) {
                this.clearPermissionSelection();
            }
        });
        document.addEventListener('pointerdown', this.onStatusDocumentPointerDown, true);
        this.init();
    }

    onStatusToggleClick = (): void => {
        this.isStatusMenuOpen(!this.isStatusMenuOpen());
    };

    onSelectStatus = (tab: nsUserPermissions.TPermissionStatusFilter): void => {
        this.permissionStatusFilter(tab);
        this.isStatusMenuOpen(false);
    };

    onStatusToggleKeyDown = (_: unknown, event: KeyboardEvent): boolean => {
        if (event.key === 'ArrowDown' || event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            this.openStatusMenuAndFocus(0);
            return false;
        }
        if (event.key === 'ArrowUp') {
            event.preventDefault();
            this.openStatusMenuAndFocus(-1);
            return false;
        }
        if (event.key === 'Escape') {
            this.closeStatusMenuAndFocusToggle();
            return false;
        }
        return true;
    };

    onStatusMenuKeyDown = (_: unknown, event: KeyboardEvent): boolean => {
        const items = this.getStatusMenuItems();
        const currentIndex = items.indexOf(event.target as HTMLElement);
        let nextIndex: number | null = null;

        switch (event.key) {
            case 'ArrowDown':
                nextIndex = currentIndex < items.length - 1 ? currentIndex + 1 : 0;
                break;
            case 'ArrowUp':
                nextIndex = currentIndex > 0 ? currentIndex - 1 : items.length - 1;
                break;
            case 'Home':
                nextIndex = 0;
                break;
            case 'End':
                nextIndex = items.length - 1;
                break;
            case 'Escape':
                event.preventDefault();
                this.closeStatusMenuAndFocusToggle();
                return false;
            case 'Tab':
                this.isStatusMenuOpen(false);
                return true;
            default:
                return true;
        }

        event.preventDefault();
        items[nextIndex]?.focus();
        return false;
    };

    onStatusFocusOut = (_: unknown, event: FocusEvent): boolean => {
        const container = document.getElementById(this.statusMenuId);
        if (!container?.contains(event.relatedTarget as Node | null)) {
            this.isStatusMenuOpen(false);
        }
        return true;
    };

    private onStatusDocumentPointerDown = (event: PointerEvent): void => {
        if (!document.getElementById(this.statusMenuId)?.contains(event.target as Node)) {
            this.isStatusMenuOpen(false);
        }
    };

    private openStatusMenuAndFocus(index: number): void {
        this.isStatusMenuOpen(true);
        requestAnimationFrame(() => {
            const items = this.getStatusMenuItems();
            items[index < 0 ? items.length - 1 : index]?.focus();
        });
    }

    private closeStatusMenuAndFocusToggle(): void {
        this.isStatusMenuOpen(false);
        document.querySelector<HTMLElement>(`#${this.statusMenuId} > button`)?.focus();
    }

    private getStatusMenuItems(): HTMLElement[] {
        return Array.from(
            document.querySelectorAll<HTMLElement>(
                `#${this.statusMenuPanelId} > [role="menuitemradio"]:not(:disabled)`,
            ),
        );
    }

    private init(): void {
        this.isReady(false);
        this.organizations().forEach(p => p.dispose());
        this.organizations([]);
        this.editingRow(null);
        this.isBulkEditing(false);
        this.selectedPermissions([]);
        this.isBusy(false);
        this.closeAddTenantForm();
        this.messageData({message: REQUEST_MESSAGE});

        Promise.all([
            this.__requestRouteData!.request() as Promise<IObjectData[]>,
            nsSyncFields.getSyncfieldIdsFromNames(ObjectTypeId.Department, ...DEPARTMENT_SEARCH_COLS),
        ])
            .then(([data, departmentSyncfieldIds]) => {
                const loadedUser = data[0];
                if (!loadedUser) {
                    throw new Error('User not found');
                }
                this.user = loadedUser;
                this.departmentSyncfieldIds = departmentSyncfieldIds;
                this.rebuildPanels(true);
                this.messageData(null);
                this.isReady(true);
            })
            .catch((reason: string) => {
                this.messageData(createErrorMessage(String(reason)));
            });
    }

    private rebuildPanels(expandSelected: boolean): void {
        this.organizations().forEach(p => p.dispose());

        const selectedOrgId = global.login.selectedOrganizationId();
        const panels = _.sortBy(
            this.buildPermissionPanelOrganizations(),
            [
                (o: IOrganization) => o.organizationid == selectedOrgId ? 0 : 1,
                (o: IOrganization) => o.organizationame.toLowerCase(),
            ],
        ).map((o: IOrganization) => this.createOrganizationPanel(o));

        this.organizations(panels);
        if (expandSelected) {
            void panels.find((p: OrganizationPanel) => p.organization.organizationid == selectedOrgId)?.expand();
        }
    }

    /**
     * Orgs with visible permissions, plus a sticky shell for the selected login org
     * so Add permission is available when the user has none yet.
     */
    private buildPermissionPanelOrganizations(): IOrganization[] {
        const overrides = this.user.overrides ?? [];
        const accessibleOrgs = (this.user.permission ?? [])
            .filter((o: IOrganization) => !!global.login.getOrganizationById(o.organizationid));
        const allMerged = nsUserPermissions.mergeOrganizationsForPermissionPanels(
            accessibleOrgs,
            overrides,
        );
        const selectedOrgId = global.login.selectedOrganizationId();
        const mergedOrgs = allMerged.filter((o: IOrganization) =>
            o.organizationid == selectedOrgId
            || nsUserPermissions.filterVisibleSystemPermissions(
                o.systempermission ?? [],
                overrides,
            ).length > 0
        );

        if (selectedOrgId && !mergedOrgs.some(o => o.organizationid == selectedOrgId)) {
            const loginOrg = global.login.getOrganizationById(selectedOrgId);
            if (loginOrg) {
                const shell = _.cloneDeep(loginOrg);
                shell.systempermission = [];
                mergedOrgs.push(shell);
            }
        }

        return mergedOrgs;
    }

    /** Selected login org stays on the page even with zero visible permissions. */
    private isSelectedOrganizationPanel(panel: OrganizationPanel): boolean {
        const selectedOrgId = global.login.selectedOrganizationId();
        return !!selectedOrgId && panel.organization.organizationid == selectedOrgId;
    }

    private allPermissionRows(): PermissionRow[] {
        return this.organizations().flatMap(org =>
            org.systemGroups().flatMap(g => g.permissions),
        );
    }

    private collectSelectedRows(): PermissionRow[] {
        return this.allPermissionRows().filter(r => r.isSelected() && r.showSelectionCheckbox());
    }

    private clearPermissionSelection(): void {
        this.allPermissionRows().forEach(r => r.isSelected(false));
    }

    private beginBulkEdit(rows: PermissionRow[]): void {
        this.isBulkEditing(true);
        rows.forEach(row => {
            if (!row.isEditing()) {
                row.startEditing();
            } else {
                row.isExpanded(false);
            }
        });
    }

    private cancelBulkEdit(rows: PermissionRow[]): void {
        rows.forEach(row => {
            if (row.isEditing()) {
                row.cancelEditing();
            }
        });
        this.isBulkEditing(false);
    }

    /** Policy check shared by single-row and bulk save; shows a message and returns false when denied. */
    private validatePermissionDraftOrMessage(row: PermissionRow): boolean {
        const validation = nsUserPermissionPolicy.validatePermissionDraft(
            row.toPolicyIdentity(),
            this.getSiblingIdentities(row),
            row.system,
        );
        if (!validation.allowed) {
            this.messageData(createErrorMessage(nsUserPermissionPolicy.tooltipForReasons(validation.reasons)));
            return false;
        }
        return true;
    }

    /**
     * MP draft equal to a soft-removed AP (no expiry) and policy allows deleteoverride → restore target.
     * Otherwise null (normal MP save).
     */
    private resolveAutoRestoreOverride(row: PermissionRow, panel: OrganizationPanel): IOverride | null {
        if (!row.isManual || !this.user) {
            return null;
        }
        const draft = this.mutations.buildManualUpdateFromRow(row);
        if (!draft) {
            return null;
        }
        const override = nsUserPermissions.findAutoRestoreOverrideForManualDraft(
            draft,
            panel.organization.systempermission ?? [],
            this.user.overrides ?? [],
        );
        if (!override?.id || !row.canDeleteOverrideForAutoRestore(override)) {
            return null;
        }
        return override;
    }

    /** Persist one row's edit (manual update or automatic→manual); finishes editing on success. */
    private persistRowSave(
        row: PermissionRow,
        panel: OrganizationPanel,
        orgIds: Set<string>,
    ): Promise<'local' | 'refresh'> {
        const needsRefresh = !row.isManual;
        const work = row.isManual
            ? this.mutations.saveManualPermission(this.user, row, panel, orgIds)
            : this.mutations.saveAutomaticAsManual(this.user, row, panel, orgIds);
        return work.then(() => {
            row.finishEditing();
            return needsRefresh ? 'refresh' : 'local';
        });
    }

    /** Delete one row (manual or automatic) and drop an empty org panel when needed. */
    private persistRowDelete(
        row: PermissionRow,
        panel: OrganizationPanel,
        orgIds: Set<string>,
    ): Promise<unknown> {
        if (row.isManual) {
            return this.mutations.deleteManualPermission(this.user, row, panel, orgIds)
                .then(() => {
                    this.removePanelIfEmpty(panel);
                });
        }
        return this.applyAutomaticDelete(row, panel, true);
    }

    /**
     * Automatic delete: createoverride + patch user/panel. Snackbar optional so bulk can toast once.
     */
    private applyAutomaticDelete(
        row: PermissionRow,
        panel: OrganizationPanel,
        snackbar = false,
    ): Promise<void> {
        const previousOverrideId = row.getOwnOverride()?.id;
        return this.mutations.deleteAutomaticPermission(this.user, row)
            .then((created) => {
                this.upsertUserOverride(created, previousOverrideId);
                panel.refreshRowsFromUser(this.user);
                this.removePanelIfEmpty(panel);
                if (snackbar) {
                    permissionSnackbar('removed');
                }
            });
    }

    /**
     * Validate and persist dirty selected rows only.
     * Manual replaces + AP→MP appends share one updatepermission; remove-overrides stay one-by-one.
     */
    private async saveBulkEdit(rows: PermissionRow[], ctx: IBulkActionCtx): Promise<void> {
        const dirtyRows = rows.filter(r => r.isDirty());
        if (!dirtyRows.length) {
            this.messageData(createErrorMessage(lm.t('user.permissions.bulk.error.save.invalid') ?? 'Invalid'));
            throw new Error('bulk save invalid');
        }
        for (const row of dirtyRows) {
            if (!row.canSave()) {
                this.messageData(createErrorMessage(lm.t('user.permissions.bulk.error.save.invalid') ?? 'Invalid'));
                throw new Error('bulk save invalid');
            }
            if (!this.validatePermissionDraftOrMessage(row)) {
                throw new Error('bulk save denied');
            }
        }

        const orgIds = this.visibleOrganizationIds();
        this.isBusy(true);
        try {
            const ops: ManualSpOp[] = [];
            const autoRestoreOverrides: IOverride[] = [];
            let appendedAutomatic = false;
            const total = dirtyRows.length;

            for (const [i, row] of dirtyRows.entries()) {
                if (ctx.signal.aborted) {
                    throw new DOMException('Aborted', 'AbortError');
                }
                ctx.reportProgress(`user.permissions.bulk.progress.saving|current:${i + 1}|total:${total}`);
                const panel = this.findPanelForRow(row);
                if (!panel) {
                    continue;
                }
                if (row.isManual) {
                    const updated = this.mutations.buildManualUpdateFromRow(row);
                    if (!updated) {
                        throw new Error(lm.t('user.permissions.deny.invalid') ?? 'user.permissions.deny.invalid');
                    }
                    // No confirm in bulk: MP equal to soft-removed AP → delete MP + later deleteoverride.
                    const autoRestore = this.resolveAutoRestoreOverride(row, panel);
                    if (autoRestore) {
                        ops.push({
                            kind: 'delete',
                            organizationId: panel.organization.organizationid,
                            panel,
                            deletedId: row.systemPermission.id,
                        });
                        autoRestoreOverrides.push(autoRestore);
                        continue;
                    }
                    ops.push({
                        kind: 'replace',
                        organizationId: panel.organization.organizationid,
                        panel,
                        previousId: row.systemPermission.id,
                        updated,
                    });
                    continue;
                }
                // createoverride/deleteoverride per AP; new MP joins the shared updatepermission below.
                const newManual = await this.mutations.prepareAutomaticAsManual(this.user, row);
                ops.push({
                    kind: 'append',
                    organizationId: panel.organization.organizationid,
                    sp: newManual,
                });
                appendedAutomatic = true;
            }

            if (ctx.signal.aborted) {
                throw new DOMException('Aborted', 'AbortError');
            }

            if (ops.length) {
                await this.mutations.saveManualSpOps(this.user, orgIds, ops);
            }
            if (autoRestoreOverrides.length) {
                await this.mutations.deletePermissionOverridesSilent(autoRestoreOverrides);
            }

            for (const row of rows) {
                row.finishEditing();
            }
            // New MP ids / restored APs need getobject — keep bulk chrome until refresh settles.
            if (appendedAutomatic || autoRestoreOverrides.length) {
                await this.refreshPanelsKeepingState();
            }
            this.isBulkEditing(false);
            if (autoRestoreOverrides.length) {
                permissionSnackbar('restored', autoRestoreOverrides.length);
            }
            const savedCount = dirtyRows.length - autoRestoreOverrides.length;
            if (savedCount) {
                permissionSnackbar('saved', savedCount);
            }
        } catch (reason: unknown) {
            if (this.isAbortError(reason)) {
                throw reason;
            }
            // Validation paths already set messageData before throwing.
            if (!(reason instanceof Error && /^bulk save (invalid|denied)$/.test(reason.message))) {
                this.messageData(createErrorMessage(String(reason)));
            }
            throw reason;
        } finally {
            this.isBusy(false);
        }
    }

    /**
     * Confirm then delete; Promise settles when dialog is cancelled or work finishes
     * so the bulk-action-bar can await real completion.
     */
    private requestBulkDelete(rows: PermissionRow[], ctx: IBulkActionCtx): Promise<void> {
        if (this.isBusy() || this.isAddMode() || this.editingRow() || this.isBulkEditing() || !rows.length) {
            return Promise.reject(new Error('bulk delete unavailable'));
        }

        // Lock the page for the whole confirm + work window (canMutateRows / row actions).
        this.isBusy(true);

        return new Promise((resolve, reject) => {
            let settled = false;
            const cancelDialog = (): void => {
                if (settled) {
                    return;
                }
                settled = true;
                this.isBusy(false);
                reject(new DOMException('Cancelled', 'AbortError'));
            };

            this.confirmAction({
                confirmKey: `user.permissions.confirm.delete|n:${rows.length}`,
                confirmLabel: 'user.permissions.btn.delete',
                doClose: cancelDialog,
                onConfirm: () => {
                    if (settled) {
                        return;
                    }
                    // Confirm chosen — ignore later hidden.bs.modal → doClose.
                    settled = true;
                    const orgIds = this.visibleOrganizationIds();
                    void this.runBulkDeleteWork(rows.slice(), ctx, orgIds)
                        .then(resolve, reject)
                        .finally(() => this.isBusy(false));
                },
            });
        });
    }

    /** Partition + delete selected rows; reports progress and honors AbortSignal. */
    private async runBulkDeleteWork(
        toDelete: PermissionRow[],
        ctx: IBulkActionCtx,
        orgIds: Set<string>,
    ): Promise<void> {
        const manualOps: ManualSpOp[] = [];
        const automaticRows: {row: PermissionRow; panel: OrganizationPanel}[] = [];

        for (const row of toDelete) {
            const panel = this.findPanelForRow(row);
            if (!panel) {
                continue;
            }
            if (row.isManual) {
                manualOps.push({
                    kind: 'delete',
                    organizationId: panel.organization.organizationid,
                    panel,
                    deletedId: row.systemPermission.id,
                });
            } else {
                automaticRows.push({row, panel});
            }
        }

        const totalSteps = (manualOps.length ? 1 : 0) + automaticRows.length;
        let step = 0;
        const nextStep = (): void => {
            if (ctx.signal.aborted) {
                throw new DOMException('Aborted', 'AbortError');
            }
            step += 1;
            ctx.reportProgress(`user.permissions.bulk.progress.deleting|current:${step}|total:${totalSteps}`);
        };

        try {
            // Reused row instances keep expand state across reconcile — collapse before delete.
            toDelete.forEach(row => row.isExpanded(false));

            if (manualOps.length) {
                nextStep();
                await this.mutations.saveManualSpOps(this.user, orgIds, manualOps);
                for (const op of manualOps) {
                    if (op.kind === 'delete') {
                        this.removePanelIfEmpty(op.panel);
                    }
                }
            }

            for (const {row, panel} of automaticRows) {
                nextStep();
                await this.applyAutomaticDelete(row, panel);
            }

            permissionSnackbar('removed', toDelete.length);
            this.clearPermissionSelection();
        } catch (reason: unknown) {
            if (!this.isAbortError(reason)) {
                this.messageData(createErrorMessage(String(reason)));
            }
            throw reason;
        }
    }

    private createOrganizationPanel(organization: IOrganization): OrganizationPanel {
        const panel = new OrganizationPanel(
            organization,
            this.user,
            this.searchSystemQueue,
            this.departmentSyncfieldIds,
            this.rowActions,
            this.isBusy,
            () => this.canMutateDrafts(),
            () => this.isAddMode(),
            this.permissionStatusFilter,
            () => this.onOrganizationDraftsCancelled(panel),
            () => this.submitNewPermissions(panel),
        );
        return panel;
    }

    /** Shows the inline Add eAdm Tenant form below the footer button (orgs already on the page are excluded). */
    private onAddTenantClick(): void {
        if (!this.canAddTenant()) {
            return;
        }
        const presentIds = this.visibleOrganizationIds();
        this.availableAddTenantOrganizations(
            (global.login.organizations() ?? []).filter(
                (o: IOrganization) => !presentIds.has(o.organizationid),
            ),
        );
        this.selectedAddTenantOrganization(null);
        this.addTenantComboboxInput('');
        this.isAddTenantFormOpen(true);
    }

    private onCancelAddTenantClick(): void {
        this.closeAddTenantForm();
    }

    private onConfirmAddTenantClick(): void {
        const organization = this.selectedAddTenantOrganization();
        if (!organization || !this.canConfirmAddTenant()) {
            return;
        }
        this.addEphemeralTenant(organization);
        this.closeAddTenantForm();
    }

    private closeAddTenantForm(): void {
        this.isAddTenantFormOpen(false);
        this.selectedAddTenantOrganization(null);
        this.addTenantComboboxInput('');
        this.availableAddTenantOrganizations([]);
    }

    /**
     * Insert a login-org shell after the selected tenant, alphabetically among the rest.
     * Expand + load, then open an empty permission draft. Cancel removes the ephemeral accordion.
     */
    private addEphemeralTenant(loginOrganization: IOrganization): void {
        // Form is still open here (add-mode); do not require canAddTenant.
        if (this.isBusy() || this.editingRow() != null) {
            return;
        }
        if (this.organizations().some(p => p.organization.organizationid == loginOrganization.organizationid)) {
            return;
        }

        const organization = _.cloneDeep(loginOrganization);
        organization.systempermission = [];
        const panel = this.createOrganizationPanel(organization);
        panel.isEphemeralTenant(true);

        const panels = this.organizations();
        const selectedOrgId = global.login.selectedOrganizationId();
        const selectedIdx = panels.findIndex(p => p.organization.organizationid == selectedOrgId);
        // Selected stays first (loadPanels order); new tenant never inserts before it.
        const afterSelected = selectedIdx >= 0 ? selectedIdx + 1 : 0;
        const name = organization.organizationame.toLowerCase();
        const offset = panels.slice(afterSelected).findIndex(
            p => p.organization.organizationame.toLowerCase() > name,
        );
        const insertAt = offset < 0 ? panels.length : afterSelected + offset;
        this.organizations.splice(insertAt, 0, panel);
        this.scrollPanelIntoView(panel);

        void panel.expand()
            .then(() => {
                if (!this.organizations().includes(panel)) {
                    return;
                }
                panel.ensureEmptyPermissionDraft();
            })
            .catch((reason: unknown) => {
                this.removeOrganizationPanel(panel);
                this.messageData(createErrorMessage(String(reason)));
            });
    }

    private onOrganizationDraftsCancelled(panel: OrganizationPanel): void {
        if (panel.isEphemeralTenant()) {
            this.removeOrganizationPanel(panel);
        }
    }

    /** Persist complete New Permission drafts, then clear add-mode for that org. */
    private submitNewPermissions(panel: OrganizationPanel): void {
        if (!panel.canSubmitNewPermissions()) {
            return;
        }
        const newPermissions = panel.getCompleteDraftManualPermissions();
        if (!newPermissions.length) {
            return;
        }
        const organizationId = panel.organization.organizationid;
        const fingerprints = newPermissions.map(sp => ({
            systemId: sp.system?.id ?? 0,
            roleId: sp.role?.id ?? 0,
            managerOf: sp.managerOf ?? 0,
        }));
        const orgIds = this.visibleOrganizationIds();
        this.runBusy(() => {
            return this.mutations.addManualPermissions(this.user, panel, newPermissions, orgIds)
                .then(() => {
                    panel.discardNewPermissionDrafts();
                    panel.isEphemeralTenant(false);
                    return this.refreshPanelsKeepingState();
                })
                .then(() => {
                    this.scrollAddedPermissionsIntoView(organizationId, fingerprints);
                });
        });
    }

    private removeOrganizationPanel(panel: OrganizationPanel): void {
        this.organizations.remove(panel);
        panel.dispose();
    }

    /** Drop the tenant accordion when it has no visible permissions and no drafts. */
    private removePanelIfEmpty(panel: OrganizationPanel): void {
        if (panel.newPermissionRows().length || panel.hasVisiblePermissions()) {
            return;
        }
        // Keep the current-org shell so Add permission stays available when empty.
        if (this.isSelectedOrganizationPanel(panel)) {
            return;
        }
        if (this.user.permission) {
            this.user.permission = this.user.permission.filter(
                o => o.organizationid != panel.organization.organizationid,
            );
        }
        this.removeOrganizationPanel(panel);
    }

    /** Run after the next two animation frames so Knockout has painted. */
    private afterPaint(fn: () => void): void {
        requestAnimationFrame(() => {
            requestAnimationFrame(fn);
        });
    }

    private scrollPanelIntoView(panel: OrganizationPanel): void {
        this.afterPaint(() => {
            document.getElementById(panel.accordionDomId)
                ?.scrollIntoView({behavior: 'smooth', block: 'center'});
        });
    }

    /**
     * After add + refresh, scroll the first matching list row into view
     * (falls back to the org accordion if rows are not ready yet).
     */
    private scrollAddedPermissionsIntoView(
        organizationId: string,
        fingerprints: ReadonlyArray<{systemId: number; roleId: number; managerOf: number}>,
    ): void {
        const panel = this.organizations().find(p => p.organization.organizationid == organizationId);
        if (!panel) {
            return;
        }

        const scroll = (): void => {
            const match = panel.systemGroups()
                .flatMap(g => g.permissions)
                .find(row => fingerprints.some(fp =>
                    (row.system?.id ?? row.systemPermission.system?.id ?? 0) == fp.systemId
                    && (row.role()?.id ?? row.systemPermission.role?.id ?? 0) == fp.roleId
                    && (row.department()?.id ?? row.systemPermission.managerOf ?? 0) == fp.managerOf
                ));
            const el = match
                ? document.getElementById(match.getDomId())
                : document.getElementById(panel.accordionDomId);
            el?.scrollIntoView({behavior: 'smooth', block: 'center'});
        };

        const whenReady = panel.isSystemsReady()
            ? Promise.resolve()
            : panel.expand();
        void whenReady.then(() => this.afterPaint(scroll));
    }

    private visibleOrganizationIds(): Set<string> {
        return new Set(this.organizations().map(p => p.organization.organizationid));
    }

    private findPanelForRow(row: PermissionRow): OrganizationPanel | null {
        return this.organizations().find(panel =>
            panel.systemGroups().some(g => g.permissions.includes(row)),
        ) ?? null;
    }

    private getSiblingIdentities(row: PermissionRow): nsUserPermissionPolicy.IPermissionIdentity[] {
        const panel = this.findPanelForRow(row);
        if (!panel) {
            return [];
        }
        return panel.systemGroups()
            .flatMap(g => g.permissions)
            .filter(r => r !== row)
            .map(r => r.toPolicyIdentity());
    }

    private beginEdit(row: PermissionRow): void {
        if (!this.canStartRowAction(row, 'edit')) {
            return;
        }
        this.editingRow(row);
        row.startEditing();
    }

    /** Seed a New Permission draft from an existing row (expand org first if needed). */
    private requestCopy(row: PermissionRow): void {
        if (!this.canStartRowAction(row, 'copy')) {
            return;
        }
        const panel = this.findPanelForRow(row);
        if (!panel || !panel.canAddDraftRows()) {
            return;
        }
        void panel.expand().then(() => {
            if (!row.canCopyAction() || !panel.canAddDraftRows()) {
                return;
            }
            panel.copyPermissionFrom(row);
        });
    }

    private cancelEdit(row: PermissionRow): void {
        if (this.isBulkEditing()) {
            return;
        }
        if (this.editingRow() !== row) {
            return;
        }
        row.cancelEditing();
        this.editingRow(null);
    }

    private saveEdit(row: PermissionRow): void {
        if (this.isBulkEditing()) {
            return;
        }
        if (this.editingRow() !== row || !row.canSave()) {
            return;
        }
        const panel = this.findPanelForRow(row);
        if (!panel || !this.validatePermissionDraftOrMessage(row)) {
            return;
        }
        const orgIds = this.visibleOrganizationIds();
        const autoRestoreOverride = this.resolveAutoRestoreOverride(row, panel);
        if (autoRestoreOverride) {
            this.confirmAction({
                confirmKey: 'user.permissions.confirm.save.as.restore',
                confirmLabel: 'user.permissions.btn.restore',
                onConfirm: () => {
                    this.runBusy(() =>
                        this.mutations
                            .restoreModifiedPair(
                                this.user,
                                row,
                                panel,
                                orgIds,
                                autoRestoreOverride,
                            )
                            .then(() => {
                                row.finishEditing();
                                this.editingRow(null);
                                return this.refreshPanelsKeepingState();
                            }),
                    );
                },
            });
            return;
        }
        this.runBusy(() => {
            return this.persistRowSave(row, panel, orgIds).then((mode) => {
                this.editingRow(null);
                // AP→MP creates a new SP id that updatepermission does not echo.
                if (mode === 'refresh') {
                    return this.refreshPanelsKeepingState();
                }
            });
        });
    }

    /** Inline permission-note save from the expanded card. */
    private savePermissionComment(row: PermissionRow): void {
        const panel = this.findPanelForRow(row);
        if (!panel || !row.isManual) {
            return;
        }
        row.isEditingPermissionComment(false);
        row.isSavingPermissionComment(true);
        this.runBusy(() => {
            return this.mutations
                .savePermissionComment(this.user!, row, panel, this.visibleOrganizationIds())
                .finally(() => row.isSavingPermissionComment(false));
        });
    }

    private requestDelete(row: PermissionRow): void {
        if (!this.canStartRowAction(row, 'delete')) {
            return;
        }
        const panel = this.findPanelForRow(row);
        if (!panel) {
            return;
        }
        // Modified-pair undo: restore dialog → delete MP then paired R override (not bulk).
        if (row.isModifiedPair()) {
            const occupancy = row.evaluateRestoreOccupancy();
            if (!occupancy.allowed) {
                this.openRestoreBlockedDialog(occupancy.reasons);
                return;
            }
            this.confirmAction({
                confirmKey: 'user.permissions.confirm.restore',
                confirmLabel: 'user.permissions.btn.restore',
                onConfirm: () => {
                    row.isExpanded(false);
                    const orgIds = this.visibleOrganizationIds();
                    this.runBusy(() =>
                        this.mutations.restoreModifiedPair(this.user, row, panel, orgIds)
                            .then(() => this.refreshPanelsKeepingState()),
                    );
                },
            });
            return;
        }
        this.confirmAction({
            confirmKey: 'user.permissions.confirm.delete',
            confirmLabel: 'user.permissions.btn.delete',
            onConfirm: () => {
                row.isExpanded(false);
                const orgIds = this.visibleOrganizationIds();
                this.runBusy(() => this.persistRowDelete(row, panel, orgIds));
            },
        });
    }

    private requestRestore(row: PermissionRow): void {
        if (!this.canStartRowAction(row, 'restore')) {
            return;
        }
        const override = row.getOwnOverride();
        if (!override?.id) {
            return;
        }
        const pendingDeletion = isOverridePendingDeletion(override);
        const pendingRemove = nsUserPermissions.hasActiveRemoveOverride(override)
            && !override.syncronizedimportfileid;
        if (!pendingDeletion) {
            const occupancy = row.evaluateRestoreOccupancy();
            if (!occupancy.allowed) {
                this.openRestoreBlockedDialog(occupancy.reasons);
                return;
            }
        }

        const runRestore = (): void => {
            row.isExpanded(false);
            // Pending soft-delete → restoreoverride; active R → deleteoverride. getobject is source of truth.
            this.runBusy(() => {
                const work = pendingDeletion
                    ? this.mutations.restorePermissionOverride(override)
                    : this.mutations.deletePermissionOverride(override);
                return work.then(() => this.refreshPanelsKeepingState());
            });
        };

        // *.delete.pending: add → cancel pending delete; remove → cancel pending restore.
        // remove.pending → cancel pending delete; else Restore synced R.
        if (pendingDeletion) {
            const kind = getCancelPendingDeletionConfirmKind(override) ?? 'restore';
            this.confirmAction({
                confirmKey: `user.permissions.confirm.cancel.pending.${kind}`,
                confirmLabel: kind === 'delete'
                    ? 'user.permissions.btn.keep.permission'
                    : 'override.btn.keep.override',
                onConfirm: runRestore,
            });
            return;
        }
        if (pendingRemove) {
            this.confirmAction({
                confirmKey: 'user.permissions.confirm.cancel.pending.delete',
                confirmLabel: 'user.permissions.btn.keep.permission',
                onConfirm: runRestore,
            });
            return;
        }
        this.confirmAction({
            confirmKey: 'user.permissions.confirm.restore',
            confirmLabel: 'user.permissions.btn.restore',
            onConfirm: runRestore,
        });
    }

    /** Page-busy / edit-mode gate shared by single-row actions. */
    private canStartRowAction(
        row: PermissionRow,
        action: 'edit' | 'delete' | 'restore' | 'copy',
    ): boolean {
        if (this.isBusy() || this.editingRow() || this.isBulkEditing()) {
            return false;
        }
        if (action !== 'copy' && this.isAddMode()) {
            return false;
        }
        switch (action) {
            case 'edit':
                return row.canEditAction();
            case 'delete':
                return row.canDeleteAction();
            case 'restore':
                return row.canRestoreAction();
            case 'copy':
                return row.canCopyAction();
        }
    }

    /** Confirm dialog from a body key; title is `${keyWithoutFlags}.title`. */
    private confirmAction(opts: {
        confirmKey: string
        confirmLabel: string
        onConfirm: () => void
        doClose?: () => void
    }): void {
        const titleKey = `${opts.confirmKey.split('|')[0]}.title`;
        this.openConfirm({
            titleKey,
            bodyHtml: `<p>${lm.t(opts.confirmKey)}</p>`,
            confirmLabel: opts.confirmLabel,
            onConfirm: opts.onConfirm,
            doClose: opts.doClose,
        });
    }

    /** Shared warning confirm chrome for single-row / bulk delete. */
    private openConfirm(opts: {
        titleKey: string
        bodyHtml: string
        confirmLabel: string
        onConfirm: () => void
        doClose?: (() => void) | undefined
    }): void {
        global.dialogManager.addConfirm({
            title: `${i('fa-exclamation-triangle')} ((${opts.titleKey}))`,
            content: opts.bodyHtml,
            doClose: opts.doClose,
            buttons: {
                confirm: {
                    label: opts.confirmLabel,
                    onClick: opts.onConfirm,
                },
                cancel: {
                    label: 'cancel',
                    onClick: opts.doClose,
                },
            },
        });
    }

    /** Occupancy blocked restore: explain why, no Yes/Continue (action cannot proceed). */
    private openRestoreBlockedDialog(reasons: nsUserPermissionPolicy.TDenyReason[]): void {
        const bodyKey = nsUserPermissionPolicy.restoreBlockedBodyKey(reasons);
        if (!bodyKey) {
            return;
        }
        global.dialogManager.addConfirm({
            title: `${i('fa-exclamation-triangle')} ((user.permissions.restore.blocked.title))`,
            content: `<p>${lm.t(bodyKey)}</p>`,
            buttons: {
                cancel: false,
                confirm: {
                    label: 'close',
                },
            },
        });
    }

    private isAbortError(reason: unknown): boolean {
        return reason instanceof DOMException && reason.name === 'AbortError';
    }

    /** Insert/replace a system-permission override on the in-memory user after createoverride. */
    private upsertUserOverride(created: IOverride, previousOverrideId?: number): void {
        const overrides = (this.user.overrides ?? [])
            .filter(o => o.id != previousOverrideId && o.id != created.id);
        overrides.push(created);
        this.user.overrides = overrides;
    }

    private runBusy(work: () => Promise<unknown>): void {
        this.isBusy(true);
        work()
            .catch((reason: unknown) => {
                if (!this.isAbortError(reason)) {
                    this.messageData(createErrorMessage(String(reason)));
                }
            })
            .finally(() => {
                this.isBusy(false);
            });
    }

    private refreshPanelsKeepingState(): Promise<void> {
        const expandedIds = new Set(
            this.organizations()
                .filter(p => p.isExpanded())
                .map(p => p.organization.organizationid),
        );

        return (this.__requestRouteData!.request() as Promise<IObjectData[]>)
            .then(data => {
                const loadedUser = data[0];
                if (!loadedUser) {
                    throw new Error('User not found');
                }
                this.user = loadedUser;

                const byId = _.keyBy(
                    this.buildPermissionPanelOrganizations(),
                    (o: IOrganization) => o.organizationid,
                );
                const panelsToRemove: OrganizationPanel[] = [];

                for (const panel of [...this.organizations()]) {
                    const next = byId[panel.organization.organizationid];
                    if (panel.isEphemeralTenant() && panel.newPermissionRows().length) {
                        continue;
                    }
                    if (!next) {
                        // Ephemeral other-tenant shells (or stale ids) drop; selected shell is in byId.
                        panelsToRemove.push(panel);
                        continue;
                    }
                    panel.applyUserData(this.user, next);
                    if (
                        !panel.hasVisiblePermissions()
                        && !panel.newPermissionRows().length
                        && !this.isSelectedOrganizationPanel(panel)
                    ) {
                        panelsToRemove.push(panel);
                    }
                }

                panelsToRemove.forEach(panel => this.removeOrganizationPanel(panel));

                // Sticky selected-org shell may be missing after a full empty refresh.
                const selectedOrgId = global.login.selectedOrganizationId();
                const selectedOrganization = selectedOrgId ? byId[selectedOrgId] : undefined;
                if (
                    selectedOrganization
                    && !this.organizations().some(p => p.organization.organizationid == selectedOrgId)
                ) {
                    const panel = this.createOrganizationPanel(selectedOrganization);
                    this.organizations.unshift(panel);
                    void panel.expand();
                }

                for (const panel of this.organizations()) {
                    if (expandedIds.has(panel.organization.organizationid) && !panel.isSystemsReady()) {
                        void panel.expand();
                    }
                }
            });
    }

    override dispose(): void {
        document.removeEventListener('pointerdown', this.onStatusDocumentPointerDown, true);
        this.selectionKosub.unsuscribeAll();
        this.selectedRowsComputed.dispose();
        this.selectedStatusLabelKey.dispose();
        this.statusTabCounts.dispose();
        this.selectedStatusBadge.dispose();
        this.organizations().forEach(panel => panel.dispose());
        this.canMutateRows.dispose();
        this.showEmptyPermissionsAlert.dispose();
        this.isAddMode.dispose();
        this.isReadMode.dispose();
        this.isRowEditing.dispose();
        this.isSelectionLocked.dispose();
        this.canMutateDrafts.dispose();
        this.canAddTenant.dispose();
        this.canConfirmAddTenant.dispose();
        this.mutations.abort();
        super.dispose();
    }
}
